import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { applyColumnResize } from '../../utils/list/utils/columnResize';
import type { ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_DEPARTURE_AIRPORT_LIST_CONFIGURATION, EMPTY_DEPARTURE_AIRPORT_LIST_CRITERIA } from "../constant/departureAirportConstant";
import { DepartureAirport } from "../objects/departureAirport";
import { DepartureAirportState } from "../objects/departureAirportState";
import { setDepartureAirportPreference } from "../utils/setDepartureAirportPreference";
import { Column } from "../objects/column";
import { DepartureAirportListCriteria } from "../objects/departureAirportTypeList";


const initialState: DepartureAirportState = {
    departure_airport_list: [],
    departure_airport_list_configuration: EMPTY_DEPARTURE_AIRPORT_LIST_CONFIGURATION,
    departure_airport_list_loading: false,
    departure_airport_list_count: 0,
    departure_airport_list_criteria: EMPTY_DEPARTURE_AIRPORT_LIST_CRITERIA,
    departure_airport: null,
    first_preference_loaded: false,
    delete_departure_airport: null,
    duplicate_departure_airport: null,
    departure_airport_list_loaded: false,
    departure_airport_list_refresh: false,
    departure_airport_is_creating: false,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const updateDepartureAirportIsCreatingReducer = (state: DepartureAirportState, action: PayloadAction<boolean>): DepartureAirportState => ({
    ...state, departure_airport_is_creating: action.payload
});
const setDeleteDepartureAirportReducer = (state: DepartureAirportState, action: PayloadAction<DepartureAirport | null>): DepartureAirportState => ({
    ...state, delete_departure_airport: action.payload
});
const setDuplicateDepartureAirportReducer = (state: DepartureAirportState, action: PayloadAction<DepartureAirport | null>): DepartureAirportState => ({
    ...state, duplicate_departure_airport: action.payload
});

const addDepartureAirportListConfigurationPreferenceReducer = (state: DepartureAirportState, action: PayloadAction<Preferences[]>): DepartureAirportState => ({
    ...state, departure_airport_list_configuration: { ...state.departure_airport_list_configuration, preferences: [...state.departure_airport_list_configuration.preferences, ...action.payload] }
});

const setDepartureAirportListConfigurationPreferenceReducer = (state: DepartureAirportState, action: PayloadAction<Preferences[]>): DepartureAirportState => ({
    ...state, departure_airport_list_configuration: { ...state.departure_airport_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: DepartureAirportState, action: PayloadAction<Preferences[]>): DepartureAirportState => {
    const { preferences, columns, order_by, order } = setDepartureAirportPreference(action.payload, state.departure_airport_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        departure_airport_list_configuration: { ...state.departure_airport_list_configuration, preferences, columns },
        departure_airport_list_criteria: { ...state.departure_airport_list_criteria, order, order_by }
    };
};

const toggleColumnReducer = (state: DepartureAirportState, action: PayloadAction<number>): DepartureAirportState => {
    const columns = cloneDeep([...state.departure_airport_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, departure_airport_list_configuration: { ...state.departure_airport_list_configuration, columns } };
};
const toggleDepartureAirportRefreshListReducer = (state: DepartureAirportState): DepartureAirportState => ({
    ...state, departure_airport_list_refresh: !state.departure_airport_list_refresh, departure_airport_list_loaded: false
});
const updateDepartureAirportListReducer = (state: DepartureAirportState, action: PayloadAction<DepartureAirport[]>): DepartureAirportState => ({
    ...state, departure_airport_list: action.payload, departure_airport_list_loaded: true
});
const updateDepartureAirportListCriteriaReducer = (state: DepartureAirportState, action: PayloadAction<DepartureAirportListCriteria>): DepartureAirportState => ({
    ...state, departure_airport_list_criteria: action.payload
});
const updateDepartureAirportReducer = (state: DepartureAirportState, action: PayloadAction<DepartureAirport | null>): DepartureAirportState => ({
    ...state, departure_airport: action.payload
});
const updateDepartureAirportListLoadingReducer = (state: DepartureAirportState, action: PayloadAction<boolean>): DepartureAirportState => ({
    ...state, departure_airport_list_loading: action.payload
});
const updateDepartureAirportListCountReducer = (state: DepartureAirportState, action: PayloadAction<number>): DepartureAirportState => ({
    ...state, departure_airport_list_count: action.payload
});

const setColumnsReducer = (state: DepartureAirportState, action: PayloadAction<Column[]>): DepartureAirportState => ({
    ...state, departure_airport_list_configuration: { ...state.departure_airport_list_configuration, columns: action.payload },
});

export const departureAirport = createSlice({
    name: 'departure_airport',
    initialState,
    reducers: {
        updateDepartureAirportIsCreating: updateDepartureAirportIsCreatingReducer,
        toggleDepartureAirportRefreshList: toggleDepartureAirportRefreshListReducer,
        setDeleteDepartureAirport: setDeleteDepartureAirportReducer,
        setDuplicateDepartureAirport: setDuplicateDepartureAirportReducer,
        addDepartureAirportListConfigurationPreference: addDepartureAirportListConfigurationPreferenceReducer,
        setDepartureAirportListConfigurationPreference: setDepartureAirportListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateDepartureAirportList: updateDepartureAirportListReducer,
        updateDepartureAirportListCriteria: updateDepartureAirportListCriteriaReducer,
        updateDepartureAirport: updateDepartureAirportReducer,
        updateDepartureAirportListLoading: updateDepartureAirportListLoadingReducer,
        updateDepartureAirportListCount: updateDepartureAirportListCountReducer,
        setColumns: setColumnsReducer,
        resizeColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.departure_airport_list_configuration.columns =
                applyColumnResize(state.departure_airport_list_configuration.columns, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    updateDepartureAirportIsCreating,
    setDeleteDepartureAirport,
    setDuplicateDepartureAirport,
    addDepartureAirportListConfigurationPreference,
    setDepartureAirportListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateDepartureAirportList,
    updateDepartureAirportListCriteria,
    updateDepartureAirport,
    updateDepartureAirportListLoading,
    updateDepartureAirportListCount,
    toggleDepartureAirportRefreshList,
    resizeColumn
} = departureAirport.actions;